import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase/config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSpecialUser, setIsSpecialUser] = useState(false);

  // Admin emails
  const ADMIN_EMAILS = [
    'sravaniayurdham@gmail.com'
  ];

  // Special user email for baby tracker
  const SPECIAL_USER_EMAIL = 'sravani.pretty.30@gmail.com';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        // Check if user is admin
        setIsAdmin(ADMIN_EMAILS.includes(user.email));
        // Check if user is special user
        setIsSpecialUser(user.email === SPECIAL_USER_EMAIL);
        
        // Get or create user profile in Firestore
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            // Create user profile
            await setDoc(userDocRef, {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || '',
              isAdmin: ADMIN_EMAILS.includes(user.email),
              createdAt: new Date()
            });
          }
        } catch (error) {
          console.error('Error creating user profile:', error);
        }
      } else {
        setCurrentUser(null);
        setIsAdmin(false);
        setIsSpecialUser(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signup = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      if (displayName) {
        await updateProfile(result.user, {
          displayName: displayName
        });
      }

      return result;
    } catch (error) {
      throw error;
    }
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  // Helper function to check if an email is an admin email
  const isAdminEmail = (email) => {
    return ADMIN_EMAILS.includes(email);
  };

  // Helper function to check if current user is the special user
  const isSpecialUserAccess = () => {
    return currentUser?.email === SPECIAL_USER_EMAIL;
  };

  const value = {
    currentUser,
    isAdmin,
    isSpecialUser,
    isAdminEmail,
    isSpecialUserAccess,
    login,
    signup,
    loginWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
